import { useEffect, useState, useMemo } from 'react'
import { Box, Flex, Text } from '@chakra-ui/react'
import { motion, AnimatePresence } from 'framer-motion'

const MotionBox = motion(Box)
const MotionFlex = motion(Flex)

interface PipelineStep {
  label: string
  detail: string
  duration: number
}

const PIPELINE: PipelineStep[] = [
  {
    label: 'Scanning website',
    detail: 'Fetching HTML, meta tags & open-graph data from the target URL',
    duration: 2800
  },
  {
    label: 'Extracting brand DNA',
    detail: 'Parsing brand name, tagline & core messaging from page content',
    duration: 3200
  },
  {
    label: 'Analyzing visual identity',
    detail: 'Identifying logo geometry, image style & visual signature patterns',
    duration: 3400
  },
  {
    label: 'Mapping color palette',
    detail: 'Extracting CSS colors, theme values & computing brand palette hierarchy',
    duration: 2600
  },
  {
    label: 'Identifying typography',
    detail: 'Detecting font families, weights & typographic scale from stylesheets',
    duration: 2400
  },
  {
    label: 'Profiling brand personality',
    detail: 'Scoring sentiment vectors, visual style & competitive positioning',
    duration: 3600
  }
]

function CheckIcon() {
  return (
    <motion.svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 400, damping: 20 }}
    >
      <path
        d="M3.5 8.5L6.5 11.5L12.5 4.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </motion.svg>
  )
}

function ProcessingIndicator() {
  return (
    <Box position="relative" w="16px" h="16px">
      <MotionBox
        position="absolute"
        top="3px"
        left="3px"
        w="10px"
        h="10px"
        borderRadius="full"
        bg="accent.500"
        animate={{ opacity: [1, 0.3, 1] }}
        transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
      />
    </Box>
  )
}

export default function AnalysisAnimation() {
  const [completedSteps, setCompletedSteps] = useState<number[]>([])
  const [activeStep, setActiveStep] = useState(0)

  const totalDuration = useMemo(
    () => PIPELINE.reduce((sum, s) => sum + s.duration, 0),
    []
  )

  const elapsedDuration = useMemo(
    () =>
      completedSteps.reduce((sum, i) => sum + PIPELINE[i].duration, 0),
    [completedSteps]
  )

  const progressPercent = Math.min(
    Math.round((elapsedDuration / totalDuration) * 100),
    100
  )

  useEffect(() => {
    if (activeStep >= PIPELINE.length) return

    const timer = setTimeout(() => {
      setCompletedSteps((prev) => [...prev, activeStep])
      setActiveStep((prev) => prev + 1)
    }, PIPELINE[activeStep].duration)

    return () => clearTimeout(timer)
  }, [activeStep])

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      h="100%"
      w="100%"
      px="24px"
    >
      <Box w="100%" maxW="520px">
        {/* Header */}
        <MotionFlex
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          alignItems="baseline"
          justifyContent="space-between"
          mb="12px"
        >
          <Text color="text.primary" textStyle="label">
            Analyzing brand
          </Text>
          <Text
            color="text.tertiary"
            fontFamily="mono"
            fontSize="11px"
            letterSpacing="0.06em"
          >
            {progressPercent}%
          </Text>
        </MotionFlex>

        {/* Progress track */}
        <MotionBox
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.15 }}
          w="100%"
          h="2px"
          bg="border.subtle"
          mb="32px"
          overflow="hidden"
          borderRadius="1px"
        >
          <Box
            h="100%"
            w={`${progressPercent}%`}
            bg="accent.500"
            transition="width 0.6s cubic-bezier(0.33, 1, 0.68, 1)"
          />
        </MotionBox>

        {/* Pipeline steps */}
        <Flex flexDirection="column" gap="0px">
          <AnimatePresence>
            {PIPELINE.map((step, i) => {
              const isCompleted = completedSteps.includes(i)
              const isActive = activeStep === i
              const isVisible = i <= activeStep

              if (!isVisible) return null

              return (
                <MotionFlex
                  key={i}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
                  alignItems="flex-start"
                  py="10px"
                  borderBottom="1px solid"
                  borderColor="border.subtle"
                >
                  {/* Step number */}
                  <Text
                    color="text.tertiary"
                    fontFamily="mono"
                    fontSize="11px"
                    w="28px"
                    flexShrink={0}
                    mt="1px"
                    letterSpacing="0.04em"
                  >
                    {String(i + 1).padStart(2, '0')}
                  </Text>

                  {/* Content */}
                  <Box flex="1" minW="0">
                    <Text
                      color={isCompleted ? 'text.secondary' : 'text.primary'}
                      textStyle="bodySmall"
                      fontWeight={isActive ? 500 : 400}
                      transition="color 0.3s ease"
                    >
                      {step.label}
                    </Text>
                    {isActive && (
                      <MotionBox
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        transition={{ duration: 0.25 }}
                      >
                        <Text
                          color="text.tertiary"
                          textStyle="bodySuperSmall"
                          mt="3px"
                        >
                          {step.detail}
                        </Text>
                      </MotionBox>
                    )}
                  </Box>

                  {/* Status indicator */}
                  <Box
                    flexShrink={0}
                    ml="12px"
                    mt="2px"
                    color={isCompleted ? 'positive' : 'accent.500'}
                  >
                    {isCompleted ? <CheckIcon /> : isActive ? <ProcessingIndicator /> : null}
                  </Box>
                </MotionFlex>
              )
            })}
          </AnimatePresence>
        </Flex>

        {/* Completion state */}
        <AnimatePresence>
          {activeStep >= PIPELINE.length && (
            <MotionFlex
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.3 }}
              mt="24px"
              alignItems="center"
              gap="8px"
            >
              <Box w="6px" h="6px" borderRadius="full" bg="positive" />
              <Text color="text.secondary" textStyle="bodySuperSmall">
                Analysis complete — preparing results
              </Text>
            </MotionFlex>
          )}
        </AnimatePresence>
      </Box>
    </Box>
  )
}
